/**
 * City callout A/B test — sticky per-visitor variant.
 *
 * Picks one of 5 Colorado cities and stores it in a long-lived cookie so
 * the same visitor sees consistent phrasing across pages and sessions.
 * The variant is attached to GA4 / Meta lead events and persisted on the
 * lead row so we can measure which city converts best.
 *
 * SSR-safe: returns a deterministic default on the server.
 */

export const CITY_VARIANTS = [
  "Denver",
  "Colorado Springs",
  "Castle Rock",
  "Pueblo",
  "Fort Collins",
] as const;

export type CityVariant = (typeof CITY_VARIANTS)[number];

const COOKIE_NAME = "cwi_city_variant";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 180; // 180 days

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split("=")[1] ?? "") : null;
}

function writeCookie(name: string, value: string) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${encodeURIComponent(
    value,
  )}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax`;
}

function isCityVariant(v: string | null | undefined): v is CityVariant {
  return !!v && (CITY_VARIANTS as readonly string[]).includes(v);
}

/**
 * Get (or assign) a sticky city variant for the current visitor.
 * Always returns a valid CityVariant, even on SSR (defaults to first city).
 */
export function getCityVariant(): CityVariant {
  if (typeof window === "undefined") return CITY_VARIANTS[0];
  const existing = readCookie(COOKIE_NAME);
  if (isCityVariant(existing)) return existing;
  const pick =
    CITY_VARIANTS[Math.floor(Math.random() * CITY_VARIANTS.length)];
  writeCookie(COOKIE_NAME, pick);
  return pick;
}
